import {
  BrowserClient,
  defaultStackParser,
  getDefaultIntegrations,
  makeFetchTransport,
  withScope,
  Scope,
} from "@sentry/browser";

import { Appearance, GoCardlessConfig } from "../types";

interface UiComponentsGlobalExtras {
  publicToken?: string;
  gcEnvironment?: string;
  appearance?: Appearance;
  sessionTokenId?: string;
  organisationId?: string;
  validConfig?: boolean;
}

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

let errorTelemetryEnabled: boolean;
const uiComponentsGlobalScope = new Scope();

export function initializeSentry(config: GoCardlessConfig): void {
  errorTelemetryEnabled = config.enableErrorTelemetry !== false;

  if (!errorTelemetryEnabled) return;

  // String literals replaced by rollup-replace plugin during build
  const sentryOptions = {
    dsn: SENTRY_DSN,
    environment: `__NODE_ENV__`,
    release: `__PACKAGE_VERSION__`,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    // filter out integrations that use the global variable, this prevents
    // interfering with any other sentry instance initialised on the page
    integrations: getDefaultIntegrations({}).filter((defaultIntegration) => {
      return !["BrowserApiErrors", "Breadcrumbs", "GlobalHandlers"].includes(
        defaultIntegration.name
      );
    }),
  };

  const uiComponentsSentryClient = new BrowserClient(sentryOptions);
  uiComponentsGlobalScope.setClient(uiComponentsSentryClient);
  uiComponentsSentryClient.init();

  const sentryExtras = {
    gcEnvironment: config.environment,
    appearance: config.appearance,
  };

  setGlobalSentryExtras(sentryExtras);
}

export const setGlobalSentryExtras = (extras: UiComponentsGlobalExtras) => {
  if (!errorTelemetryEnabled) return;

  uiComponentsGlobalScope.setExtras({ ...extras });
};

export const captureSentryError = (error: unknown, extras = {}) => {
  if (!errorTelemetryEnabled) return;

  const errorScope = uiComponentsGlobalScope.clone();
  withScope(errorScope, () => {
    errorScope.setExtras(extras);
  });
  return errorScope.captureException(error);
};
